'use client';

import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import { isReviewHubPath } from '@/lib/routes/review-hub';

// Lazy load del widget de chat - no es crítico para el primer render
const AIFloatingWidget = dynamic(() => import('./AIFloatingWidget'), {
  ssr: false,
});

// Interfaz para datos de disponibilidad y precios del apartamento actual
export interface AvailabilityData {
  isAvailable: boolean;
  totalPrice: number;
  pricePerNight: number;
  nights: number;
  minStay?: number;
  reason?: string; // Para explicar por qué no está disponible (ej: "Mínimo 3 noches")
  breakdown?: {
    base: number;
    aseo: number;
  };
}

interface ChatContext {
  dates: {
    checkIn: string;
    nights: number;
    checkOut: string;
  };
  guests: number;
  query: string;
  availableApartments: Array<{
    id: string;
    name: string;
    totalPrice: number;
    pricePerNight: number;
  }>;
  currentPage?: 'home' | 'results' | 'apartment' | string;
  currentApartment?: {
    id: string;
    name: string;
    floor?: string;
    price?: number;
    capacity?: number;
    bedrooms?: number;
    description?: string;
  };
  // Datos de disponibilidad específicos del apartamento actual
  currentAvailability?: AvailabilityData | null;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface ClaudeMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: number;
}

interface GlobalAIChatContextType {
  updateContext: (context: Partial<ChatContext>) => void;
  openChat: () => void;
  closeChat: () => void;
  clearChat: () => void;
  isOpen: boolean;
}

const GlobalAIChatContext = createContext<GlobalAIChatContextType | null>(null);
let hasWarnedMissingProvider = false;

export function GlobalAIChatProvider({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  // En los hubs de reseñas NO se monta el widget de Lía
  // (herramienta enfocada); el provider sigue dando contexto a children.
  // Las páginas inglesas tienen su propio contacto en inglés.
  const hideChatWidget = isReviewHubPath(pathname) || /^\/(en|pt)\//.test(pathname ?? '');
  const [isOpen, setIsOpen] = useState(false);
  const [context, setContext] = useState<ChatContext>({
    dates: {
      checkIn: '',
      nights: 2,
      checkOut: '',
    },
    guests: 2,
    query: '',
    availableApartments: [],
    currentPage: 'home',
  });

  // Estados con persistencia en localStorage
  const [messages, setMessages] = useState<Message[]>(() => {
    // Cargar mensajes desde localStorage solo en el cliente
    if (typeof window !== 'undefined') {
      try {
        const saved = localStorage.getItem('ai_chat_messages');
        return saved ? JSON.parse(saved) : [];
      } catch (e) {
        console.error('Error al cargar mensajes del localStorage:', e);
        return [];
      }
    }
    return [];
  });

  const [claudeHistory, setClaudeHistory] = useState<ClaudeMessage[]>(() => {
    // Cargar historial de Claude desde localStorage solo en el cliente
    if (typeof window !== 'undefined') {
      try {
        const saved = localStorage.getItem('ai_chat_claude_history');
        return saved ? JSON.parse(saved) : [];
      } catch (e) {
        console.error('Error al cargar historial de Claude del localStorage:', e);
        return [];
      }
    }
    return [];
  });

  // Guardar mensajes en localStorage cuando cambien
  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem('ai_chat_messages', JSON.stringify(messages));
      } catch (e) {
        console.error('Error al guardar mensajes en localStorage:', e);
      }
    }
  }, [messages]);

  // Guardar historial de Claude en localStorage cuando cambie
  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem('ai_chat_claude_history', JSON.stringify(claudeHistory));
      } catch (e) {
        console.error('Error al guardar historial de Claude en localStorage:', e);
      }
    }
  }, [claudeHistory]);

  const updateContext = useCallback((newContext: Partial<ChatContext>) => {
    setContext(prev => ({ ...prev, ...newContext }));
  }, []);

  const openChat = () => setIsOpen(true);
  const closeChat = () => setIsOpen(false);

  const clearChat = useCallback(() => {
    // Limpiar estados
    setMessages([]);
    setClaudeHistory([]);

    // Limpiar localStorage
    if (typeof window !== 'undefined') {
      try {
        localStorage.removeItem('ai_chat_messages');
        localStorage.removeItem('ai_chat_claude_history');
      } catch (e) {
        console.error('Error al limpiar chat del localStorage:', e);
      }
    }
  }, []);

  return (
    <GlobalAIChatContext.Provider value={{ updateContext, openChat, closeChat, clearChat, isOpen }}>
      {children}

      {/* Widget flotante de Lía - renderizado con Portal. Oculto en el hub de reseñas. */}
      {!hideChatWidget && (
        <AIFloatingWidget
          isOpen={isOpen}
          onOpen={openChat}
          onClose={closeChat}
          context={context}
          messages={messages}
          claudeHistory={claudeHistory}
          onMessagesChange={setMessages}
          onClaudeHistoryChange={setClaudeHistory}
        />
      )}
    </GlobalAIChatContext.Provider>
  );
}

export function useGlobalAIChat() {
  const context = useContext(GlobalAIChatContext);
  if (!context) {
    if (!hasWarnedMissingProvider) {
      // Silenciar el crash y avisar en consola para ambientes sin provider (por ejemplo, render aislado)
      console.warn('useGlobalAIChat se usó fuera de GlobalAIChatProvider; usando no-ops.');
      hasWarnedMissingProvider = true;
    }
    const noop = () => {};
    return { updateContext: noop, openChat: noop, closeChat: noop, clearChat: noop, isOpen: false };
  }
  return context;
}
